import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Landing from "@/pages/Landing";
import ThankYou from "@/pages/ThankYou";
import TrustCenter from "@/pages/TrustCenter";
import Home from "@/pages/Home";
import Journey from "@/pages/Journey";
import StepDetail from "@/pages/StepDetail";
import SurveyPage from "@/pages/SurveyPage";
import PainScanPage from "@/pages/PainScanPage";
import VideoPage from "@/pages/VideoPage";
import DailyPracticePage from "@/pages/DailyPracticePage";
import WeekCompleteInterstitial from "@/pages/WeekCompleteInterstitial";
import JournalPage from "@/pages/JournalPage";
import JournalHistory from "@/pages/JournalHistory";
import ProgressPage from "@/pages/ProgressPage";
import Settings from "@/pages/Settings";
import NotesPage from "@/pages/NotesPage";
import FlareLogPage from "@/pages/FlareLogPage";
import WeeklyReportPage from "@/pages/WeeklyReportPage";
import MaintenancePlan from "@/pages/MaintenancePlan";
import IdentityPage from "@/pages/IdentityPage";
import Graduation from "@/pages/Graduation";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminClientDetail from "@/pages/AdminClientDetail";
import { Toaster } from "@/components/ui/sonner";

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#22D3EE] animate-spin" />
      </div>
    );
  }
  // Logged-in members never see the sales page — straight to their portal.
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  if (user && !user.onboarded) return <Navigate to="/onboarding" replace />;
  if (user) return <Navigate to="/home" replace />;
  // Anonymous visitors get the public marketing site.
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/trust-center" element={<TrustCenter />} />
            <Route path="/trust-center/:slug" element={<TrustCenter />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/journey" element={<ProtectedRoute><Journey /></ProtectedRoute>} />
            <Route path="/journey/:step" element={<ProtectedRoute><StepDetail /></ProtectedRoute>} />
            <Route path="/journey/:step/survey" element={<ProtectedRoute><SurveyPage /></ProtectedRoute>} />
            <Route path="/journey/:step/pain-scan" element={<ProtectedRoute><PainScanPage /></ProtectedRoute>} />
            <Route path="/journey/:step/video" element={<ProtectedRoute><VideoPage /></ProtectedRoute>} />
            <Route path="/journey/:step/daily-practice" element={<ProtectedRoute><DailyPracticePage /></ProtectedRoute>} />
            <Route path="/journey/:step/week-complete" element={<ProtectedRoute><WeekCompleteInterstitial /></ProtectedRoute>} />
            <Route path="/journey/:step/journal" element={<ProtectedRoute><JournalPage /></ProtectedRoute>} />
            <Route path="/journal-history" element={<ProtectedRoute><JournalHistory /></ProtectedRoute>} />
            <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/notes" element={<ProtectedRoute><NotesPage /></ProtectedRoute>} />
            <Route path="/flare-log" element={<ProtectedRoute><FlareLogPage /></ProtectedRoute>} />
            <Route path="/weekly-report" element={<ProtectedRoute><WeeklyReportPage /></ProtectedRoute>} />
            <Route path="/maintenance-plan" element={<ProtectedRoute><MaintenancePlan /></ProtectedRoute>} />
            <Route path="/identity" element={<ProtectedRoute><IdentityPage /></ProtectedRoute>} />
            <Route path="/graduation" element={<ProtectedRoute><Graduation /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/clients/:userId" element={<ProtectedRoute requireAdmin><AdminClientDetail /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-center" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
